import { useEffect, useMemo, useRef, useState } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'

import ChatView from '../../hey-nic/ChatView'
import ChatInput from '../../hey-nic/ChatInput'
import useSalaryPrompt from '../../hey-nic/useSalaryPrompt'
import useSpouseGoalPrompt from '../../hey-nic/useSpouseGoalPrompt'
import { botTurn } from '../../hey-nic/chatTurns'
import { HomeChatCard, HomeChatBody, HomeChatFooter } from './styled'

/*
 * The Home screen's embedded chatbot (wide viewports only — narrow ones get
 * HomeHeyNicComment and the full-screen route).
 *
 * Unlike SBSA's panel this is the FULL agent: a free-text input, not just a
 * pill pair. It borrows the mobile bubble layout (`compact`) because the side
 * column is a narrow container even on a wide viewport, exactly as the SBSA
 * panel does, and it has no header — the design leaves the card as one surface.
 *
 * The opening comment is FE-authored (see homeChatDefaults) and stays
 * client-side: the agent isn't called until the user taps a pill or types, and
 * nothing is persisted until then either. It is passed as a `leadingTurns` entry
 * rather than pushed into the chat so it survives the post-stream reconcile and
 * can track the user's alerts changing underneath it.
 *
 * The placeholder-salary and spouse-retired alerts each answer a pill
 * client-side as well, and their exchanges join the same list (see
 * hey-nic/salaryPrompt and hey-nic/spouseGoalPrompt). At most one is live at a
 * time — HomeAlerts picks a single owner for the pills.
 */
const HomeHeyNicChat = ({ store, comment, suggestions, salaryPrompt, spouseGoalPrompt }) => {
  const [draft, setDraft] = useState('')
  const inputRef = useRef(null)

  // This surface's own chat — never whatever happens to be in the store's shared
  // `activeChat` slot (see Chat.homeChat).
  const chat = store.homeChat
  // Any stream blocks sending (there is one at a time), whoever owns it.
  const streaming = store.responseLoading

  // Timestamp the opening comment once per mount, so it doesn't tick over on
  // every render.
  const openedAt = useMemo(() => new Date().toISOString(), [])

  /*
   * The placeholder-salary exchange, answered here rather than by the agent
   * (see hey-nic/salaryPrompt). `onSave` re-pulls the case as well as writing
   * the number: Home's projections, and this very alert, are computed from it.
   */
  const salary = useSalaryPrompt({
    enabled: salaryPrompt,
    onSave: async amount => {
      await store.updateAccount({ annualSalary: amount })
      // Social Security is estimated off salary, and the plan column shows it.
      await store.getSSBenefits()
      await store.getBaseCase()
    },
  })

  /*
   * The spouse-retired "Learn more" answer (see hey-nic/spouseGoalPrompt). It
   * matters most on this surface: the wide layout renders this panel INSTEAD of
   * the alerts box, so the help icon that would otherwise carry this copy is not
   * on the screen at all.
   */
  const spouseGoal = useSpouseGoalPrompt({ enabled: spouseGoalPrompt })

  const exchangeStarted = salary.turns.length > 0 || spouseGoal.turns.length > 0
  const promptOwner = salaryPrompt ? 'salary' : spouseGoalPrompt ? 'spouseGoal' : null

  /*
   * A handover bubble for an alert that takes the pills over AFTER an exchange
   * has already begun.
   *
   * Two things otherwise conspire to swallow it. The opening comment is pinned
   * once an exchange starts (below), so it cannot restate itself as the new
   * alert; and ChatView offers pills only on the NEWEST leading turn, so the new
   * owner's pills, sitting on that pinned opener, are never rendered at all.
   * Resolving the salary prompt used to take the spouse-retired alert down with
   * it — text and help both gone from Home, while the alerts box had already
   * stepped aside because the alert was no longer stranded.
   *
   * Announcing the handover as its own turn puts both back, and reads the way
   * the conversation actually went: you have dealt with that, here is the next.
   */
  const [handoverTurns, setHandoverTurns] = useState([])
  const announcedOwner = useRef(promptOwner)

  useEffect(() => {
    if (promptOwner === announcedOwner.current) return
    /*
     * Never mid-save. updateAccount replaces the account as soon as its PATCH
     * lands, while the rest of onSave is still running, so ownership can move
     * with the salary confirmation still to come. Announcing here would put the
     * handover BEFORE that confirmation, leaving the confirmation the newest
     * turn — and hiding the very pills this exists to show. Deferring keeps the
     * announcement last, which is also the only order that reads correctly:
     * the user has not been told the save worked yet.
     */
    if (salary.saving) return
    const handingOver = exchangeStarted && promptOwner !== null
    announcedOwner.current = promptOwner
    // Nothing to announce when no exchange has started: the opener is still
    // live, and it restates itself with the new alert's pills on its own.
    if (handingOver) setHandoverTurns(prev => [...prev, botTurn(comment, suggestions)])
  }, [promptOwner, exchangeStarted, comment, suggestions, salary.saving])

  /*
   * Ordered by when each turn was made. Deferring the announcement past a save
   * in flight (above) is what makes that the right order: a handover is stamped
   * only once the exchange before it has said its last word, and the exchange
   * after it cannot start until the user clicks the pill it carries. A tie
   * sorts the handover after the salary turn it follows and before any spouse
   * turn, which is the order that matters.
   */
  const clientTurns = [...salary.turns, ...spouseGoal.turns, ...handoverTurns].sort((a, b) =>
    a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
  )

  /*
   * The opening comment normally tracks the user's alerts changing underneath
   * it. Once the client-side exchange has started it must not: a successful save
   * clears the alert, and the first bubble would rewrite itself into the generic
   * greeting while the user is still reading the conversation it opened.
   */
  const pinnedComment = useRef(null)
  if (clientTurns.length === 0) pinnedComment.current = null
  else if (pinnedComment.current === null) pinnedComment.current = comment
  const openingComment = pinnedComment.current ?? comment

  /*
   * Load the chat this panel started earlier in the session, if any.
   *
   * `streaming` is a dependency because the store declines to load anything
   * while a stream it doesn't own is running — that would abort the reply.
   * Re-running when the flag clears is what picks the transcript up afterwards.
   */
  useEffect(() => {
    store.openHomeChat().catch(err => {
      // The panel keeps its opening comment and pills; only the saved transcript
      // is missing, and the next send starts a fresh turn.
      console.error('[HomeHeyNicChat] failed to open the home chat:', err)
    })
  }, [store, streaming])

  const send = text => {
    const trimmed = text.trim()
    if (!trimmed || streaming) return
    // Offered first: a message either exchange takes never reaches the agent,
    // is never persisted, and does not create a chat.
    if (!salary.handle(trimmed) && !spouseGoal.handle(trimmed)) {
      store.sendHomeMessage(trimmed).catch(err => {
        console.error('[HomeHeyNicChat] failed to send:', err)
      })
    }
    // Clicking a pill moves focus to the button; put it back so the user can
    // type a follow-up straight away (mirrors HeyNic.handleSuggestion).
    inputRef.current?.focus()
  }

  return (
    <HomeChatCard>
      <HomeChatBody>
        <ChatView
          compact
          flat
          hideEmptyState
          chat={chat}
          leadingTurns={[
            { role: 'bot', text: openingComment, timestamp: openedAt, suggestions },
            ...clientTurns,
          ]}
          pillsDisabled={streaming || salary.saving}
          onSuggestion={send}
        />
      </HomeChatBody>

      <HomeChatFooter>
        {/* autoFocus off: Home is not a chat page, and pulling the caret (and on
            a touch device the keyboard) into the panel on arrival would fight
            the rest of the screen. */}
        <ChatInput
          compact
          autoFocus={false}
          chat={chat}
          text={draft}
          setText={setDraft}
          inputRef={inputRef}
          onSubmit={send}
        />
      </HomeChatFooter>
    </HomeChatCard>
  )
}

HomeHeyNicChat.propTypes = {
  /** The FE-authored opening comment (see homeChatDefaults.homeChatComment). */
  comment: PropTypes.string.isRequired,
  /** Its starter pills (see homeChatDefaults.homeChatSuggestions). */
  suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** The placeholder-salary alert is live, so this surface answers its pills. */
  salaryPrompt: PropTypes.bool,
  /** The spouse-retired alert is live, so this surface answers its help pill. */
  spouseGoalPrompt: PropTypes.bool,
}

export default inject('store')(observer(HomeHeyNicChat))
